import React from 'react';
import clsx from 'clsx';
import useStyles from './styles';
import { PostCard } from 'molecules';
import { PostCardInfo } from 'types/post';
import { Skeleton } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { reportError } from 'lib/errors';
import { LoadingError } from 'atoms';

/**
 * FeaturedPostGrid Component
 *
 * Displays a 2x2 grid of featured posts using Grid2 and PostCard with variant="featured".
 * Grid owns cell size constraints (tall/short) so each card fills
 * its cell and the image area flexes to fill remaining space.
 *
 * Layout:
 * | 1 (tall) | 3 (short) |
 * | 2 (short)| 4 (tall)  |
 *
 * Root is a `div`; the Home layout wraps this in `<section>` with padding.
 *
 * @param props - position1Post..position4Post, isLoading, error
 */
const FeaturedPostGrid = (props: FeaturedPostGridPropTypes) => {
  const { classes } = useStyles();
  const { position1Post, position2Post, position3Post, position4Post, isLoading, error } =
    props;

  if (error) {
    return (
      <LoadingError
        error={error}
        failedTarget="featured posts"
        reportError={reportError}
      />
    );
  }

  const renderCell = (
    position: 1 | 2 | 3 | 4,
    post: PostCardInfo,
    paddingClasses: string,
    cellClass: string,
    skeletonClass: string,
  ) => {
    const wrapperClass = clsx(paddingClasses, cellClass);
    if (isLoading) {
      return (
        <div className={wrapperClass}>
          <div className={clsx(classes.skeletonCard, skeletonClass)}>
            <Skeleton
              variant="rectangular"
              animation="pulse"
              className={classes.skeletonImage}
              sx={{ height: '100%' }}
            />
            <div className={classes.skeletonContent}>
              <Skeleton variant="text" animation="pulse" width="60%" />
              <Skeleton variant="text" animation="pulse" width="40%" />
              <Skeleton variant="text" animation="pulse" width="80%" />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={wrapperClass}>
        <PostCard {...post} variant="featured" />
      </div>
    );
  };

  return (
    <div className={classes.root} data-component="FeaturedPostGrid">
      <Grid2 container>
        <Grid2 size={{ xs: 12, sm: 6, md: 7, lg: 8 }}>
          {renderCell(
            1,
            position1Post,
            clsx(classes.bottomPadding, classes.rightPadding),
            classes.cellTall,
            classes.skeletonLeft,
          )}
          {renderCell(
            2,
            position2Post,
            clsx(classes.rightPadding, classes.bottomPaddingMobileOnly),
            classes.cellShort,
            classes.skeletonLeft,
          )}
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 5, lg: 4 }}>
          {renderCell(
            3,
            position3Post,
            classes.bottomPadding,
            classes.cellShort,
            classes.skeletonRight,
          )}
          {renderCell(4, position4Post, '', classes.cellTall, classes.skeletonRight)}
        </Grid2>
      </Grid2>
    </div>
  );
};

export type FeaturedPostGridPropTypes = {
  position1Post: PostCardInfo;
  position2Post: PostCardInfo;
  position3Post: PostCardInfo;
  position4Post: PostCardInfo;
  isLoading: boolean;
  error?: Error | null;
};

export default FeaturedPostGrid;
